import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { BiField } from "../components/BiField";
import { SearchSelect } from "../components/SearchSelect";
import { Pager } from "../components/Pager";
import { SortableTh } from "../components/SortableTh";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useScrollLock, useModalKeys } from "../lib/useScrollLock";
import { useAutoPageSize } from "../lib/useAutoPageSize";
import { useSortable } from "../lib/useSortable";
import { usePersistedPage } from "../lib/usePersistedPage";

type Report = {
  id: number;
  source: "user" | "kexi";
  user_id: number | null;
  submission_id: number | null;
  summary_zh: string | null;
  summary_en: string | null;
  title_zh: string;
  title_en: string | null;
  report_url: string | null;
  report_type: "pdf" | "ppt" | "site";
  file_url: string | null;
  site_url: string | null;
  sector: string | null;
  pages: number | null;
  visibility: "private" | "pending" | "public";
  created_at: string;
  published_at: string | null;
  user_email: string | null;
  user_name: string | null;
  user_company: string | null;
};

type SiteUser = {
  id: number;
  email: string;
  display_name: string;
  company: string | null;
  submissions: number;
  reports: number;
};

type Draft = {
  source: "user" | "kexi";
  user_id: number | "";
  title_zh: string;
  title_en: string;
  report_url: string;
  slug: string;
  sector: string;
  pages: string;
  report_count: string;
  summary_zh: string;
  summary_en: string;
};

const EMPTY_DRAFT: Draft = {
  source: "user", user_id: "", title_zh: "", title_en: "", report_url: "", slug: "",
  sector: "", pages: "", report_count: "", summary_zh: "", summary_en: "",
};

function ReportModal({ users, onCancel, onDone }: {
  users: SiteUser[];
  onCancel: () => void;
  onDone: (r: Report) => void;
}) {
  const [d, setD] = useState<Draft>({ ...EMPTY_DRAFT });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useScrollLock(true);

  const upd = (patch: Partial<Draft>) => setD((prev) => ({ ...prev, ...patch }));

  const valid = (d.source === "kexi" || d.user_id !== "") && d.title_zh.trim() && (d.report_url.trim() || d.slug.trim());

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<Report>("/api/admin/studio/reports", {
        source: d.source,
        user_id: d.source === "kexi" ? null : Number(d.user_id),
        title_zh: d.title_zh.trim(),
        title_en: d.title_en.trim() || null,
        report_url: d.report_url.trim() || null,
        slug: d.slug.trim() || null,
        sector: d.sector.trim() || null,
        pages: d.pages ? Number(d.pages) : null,
        report_count: d.report_count ? Number(d.report_count) : null,
        summary_zh: d.summary_zh.trim() || null,
        summary_en: d.summary_en.trim() || null,
      });
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `创建失败：${JSON.stringify(e.body)}` : String(e));
    } finally {
      setSaving(false);
    }
  };
  useModalKeys(true, { onClose: onCancel, onSubmit: () => { if (valid && !saving) submit(); } });

  return (
    <div className="amodal-backdrop" onClick={onCancel}>
      <div className="amodal" style={{ width: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="amodal-head">
          <h3>新建报告</h3>
          <button className="amodal-close" onClick={onCancel}>×</button>
        </div>

        {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}

        <div className="ce-field">
          <div className="ce-label">来源</div>
          <select value={d.source} onChange={(e) => upd({ source: e.target.value as Draft["source"] })} style={{ width: "100%" }}>
            <option value="user">用户提交制作</option>
            <option value="kexi">珂玺自有研究</option>
          </select>
        </div>

        {d.source === "user" && (
          <div className="ce-field">
            <div className="ce-label">交付给用户 *</div>
            <SearchSelect
              value={d.user_id === "" ? null : d.user_id}
              onChange={(v) => upd({ user_id: v === null ? "" : Number(v) })}
              placeholder="输入姓名 / 邮箱 / 公司搜索…"
              options={users.map((u) => ({ value: u.id, label: u.display_name, hint: `${u.email}${u.company ? ` · ${u.company}` : ""}` }))}
            />
          </div>
        )}

        <BiField label="报告标题" cn={d.title_zh} en={d.title_en} onCn={(v) => upd({ title_zh: v })} onEn={(v) => upd({ title_en: v })} placeholder="报告标题" />

        <div className="ce-field">
          <div className="ce-label">报告网页链接（公开直链；pdf/pptx 含在该网页内）</div>
          <input className="ce-input" placeholder="https://..." value={d.report_url}
            onChange={(e) => upd({ report_url: e.target.value })} />
        </div>

        <div className="ce-field">
          <div className="ce-label">智库项目 slug（定向报告用，由智库方提供；与链接至少填一个）</div>
          <input className="ce-input" placeholder="如 riscv-2026" value={d.slug}
            onChange={(e) => upd({ slug: e.target.value })} />
        </div>

        <div className="ce-field">
          <div className="ce-label">赛道 / 页数 / 报告份数（页数与份数任填一个，前台按页数优先显示）</div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10 }}>
            <input className="ce-input" placeholder="赛道（如 半导体 · RISC-V）" value={d.sector} onChange={(e) => upd({ sector: e.target.value })} />
            <input className="ce-input" type="number" placeholder="页数" value={d.pages} onChange={(e) => upd({ pages: e.target.value })} />
            <input className="ce-input" type="number" placeholder="份数" value={d.report_count} onChange={(e) => upd({ report_count: e.target.value })} />
          </div>
        </div>

        <BiField label="描述（公开展示区显示，选填）" rows={4}
          cn={d.summary_zh} en={d.summary_en} onCn={(v) => upd({ summary_zh: v })} onEn={(v) => upd({ summary_en: v })} placeholder="描述" />

        <div className="amodal-actions">
          <button className="btn" onClick={onCancel}>取消</button>
          <button className="btn btn-primary" disabled={!valid || saving} onClick={submit}>
            {saving ? "创建中…" : "创建（默认私密）"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function StudioReportsPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState<Report[] | null>(null);
  const [delFor, setDelFor] = useState<Report | null>(null);
  const [users, setUsers] = useState<SiteUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = usePersistedPage("kexi_studio_reports_page");
  const { containerRef, pageSize } = useAutoPageSize();

  useEffect(() => {
    api.get<Report[]>("/api/admin/studio/reports").then(setRows).catch((e) => setError(String(e)));
    api.get<SiteUser[]>("/api/admin/studio/users").then(setUsers).catch(() => {});
  }, []);

  const setVisibility = async (id: number, visibility: Report["visibility"]) => {
    try {
      const updated = await api.patch<Report>(`/api/admin/studio/reports/${id}`, { visibility });
      setRows((prev) => prev?.map((r) => (r.id === id ? updated : r)) ?? null);
    } catch (e) {
      const code = e instanceof ApiError ? (e.body as { error?: string } | null)?.error : null;
      setError(code === "report_url_required_for_public"
        ? "自动获取公开链接失败（智库不可达或 slug 无效）：请稍后重试，或进入报告明细手动填写「报告网页链接」后再批准公开。"
        : code === "intel_project_not_public"
          ? "该项目在智库侧尚未公开：请先在智库仓库 projects.js 给该项目加 public: true 并部署（同步 robots.txt），再批准公开，否则公开链接会被登录墙拦截。"
          : e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : String(e));
    }
  };

  const doRemove = async () => {
    if (!delFor) return;
    const target = delFor;
    setDelFor(null);
    try {
      await api.delete(`/api/admin/studio/reports/${target.id}`);
      setRows((prev) => prev?.filter((x) => x.id !== target.id) ?? null);
    } catch (e) {
      setError(String(e));
    }
  };

  const { sorted, sortKey, sortDir, toggle } = useSortable(rows ?? []);
  const pageRows = rows ? sorted.slice((page - 1) * pageSize, page * pageSize) : null;
  const pendingCount = rows?.filter((r) => r.visibility === "pending").length ?? 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">智库报告</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
            制作好的报告挂载给用户（默认私密）；用户申请公开后在此审核
            {pendingCount > 0 && <span className="ce-dirty" style={{ marginLeft: 10 }}>{pendingCount} 份待审核</span>}
          </div>
        </div>
        <div className="page-actions" style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ 新建报告</button>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
      {!rows && !error && <div className="muted">加载中…</div>}
      {rows && rows.length === 0 && <div className="muted">暂无报告，点右上「+ 新建报告」挂载第一份</div>}

      <div ref={containerRef}>
      {rows && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              {(() => { const s = { active: sortKey, dir: sortDir, toggle }; return <>
              <SortableTh sortKey="created_at" {...s} style={{ width: 100 }}>时间</SortableTh>
              <SortableTh sortKey="title_zh" {...s}>标题</SortableTh>
              <SortableTh sortKey="user_name" {...s} style={{ width: 140 }}>用户</SortableTh>
              <SortableTh sortKey="sector" {...s} style={{ width: 130 }}>赛道</SortableTh>
              <SortableTh sortKey="visibility" {...s} style={{ width: 80 }}>可见性</SortableTh>
              <th style={{ width: 230 }}></th>
              </>; })()}
            </tr>
          </thead>
          <tbody>
            {pageRows!.map((r) => (
              <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => nav(`/studio/reports/${r.id}`)}>
                <td className="muted">{new Date(r.created_at).toLocaleDateString("zh-CN")}</td>
                <td>
                  {r.title_zh}
                  {r.title_en && <div className="muted" style={{ fontSize: 11 }}>{r.title_en}</div>}
                </td>
                <td>
                  {r.source === "kexi"
                    ? <span className="pill">珂玺研究</span>
                    : <>{r.user_name}{r.user_company && <div className="muted" style={{ fontSize: 11 }}>{r.user_company}</div>}</>}
                </td>
                <td>{r.sector || <span className="muted">—</span>}</td>
                <td>
                  {r.visibility === "pending"
                    ? <span className="ce-dirty">待审核</span>
                    : r.visibility === "public"
                      ? <span className="success">已公开</span>
                      : <span className="muted">私密</span>}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    {r.visibility !== "public" && (
                      <button className="btn btn-xs" onClick={() => setVisibility(r.id, "public")}>
                        {r.visibility === "pending" ? "批准公开" : "直接公开"}
                      </button>
                    )}
                    {r.visibility !== "private" && (
                      <button className="btn btn-xs" onClick={() => setVisibility(r.id, "private")}>设为私密</button>
                    )}
                    <button className="btn btn-xs btn-danger" onClick={() => setDelFor(r)}>删除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rows && <Pager page={page} pageSize={pageSize} total={rows.length} onChange={setPage} />}
      </div>

      {showModal && (
        <ReportModal
          users={users}
          onCancel={() => setShowModal(false)}
          onDone={(r) => { setRows((prev) => [r, ...(prev ?? [])]); setShowModal(false); }}
        />
      )}

      <ConfirmDialog
        open={delFor !== null}
        title="删除报告"
        message={delFor ? <>确认删除报告「{delFor.title_zh}」？报告及其文件将被永久删除，不可恢复。</> : null}
        confirmLabel="删除"
        danger
        onConfirm={doRemove}
        onCancel={() => setDelFor(null)}
      />
    </div>
  );
}
